import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RiCloseLine, RiCoinLine, RiCheckLine, RiStarLine, RiExternalLinkLine } from 'react-icons/ri';
import { stripeService } from '../../services/stripeService';
import { useCredits } from '../../context/CreditContext';

const PLANS = [
    {
        key:        'standard' as const,
        label:      'Standard',
        price:      '$9.99/mo',
        credits:    '1,000 credits',
        highlight:  true,
        features:   ['~10 resumes or 6 AI interviews', 'All features unlocked', 'Monthly renewal'],
    },
    {
        key:        'premium' as const,
        label:      'Premium',
        price:      '$24.99/mo',
        credits:    '3,500 credits',
        highlight:  false,
        features:   ['~35 resumes or 23 AI interviews', '20% more value vs Standard', 'Priority support'],
    },
];

export default function PaywallModal() {
    const { showPaywall, closePaywall, refresh } = useCredits();
    const [loading, setLoading] = useState<string | null>(null);
    const [error, setError]     = useState<string | null>(null);

    const handleUpgrade = async (planKey: 'standard' | 'premium') => {
        setLoading(planKey);
        setError(null);
        try {
            const url = await stripeService.createCheckout(planKey);
            window.location.href = url;
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to start checkout');
            setLoading(null);
        }
    };

    return (
        <AnimatePresence>
            {showPaywall && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        key="paywall-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closePaywall}
                        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
                    />

                    {/* Modal */}
                    <motion.div
                        key="paywall-modal"
                        initial={{ opacity: 0, scale: 0.96, y: 16 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 16 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                    >
                        <div className="relative w-full max-w-lg bg-[#0f0f14] border border-white/[0.1] rounded-3xl p-6 shadow-2xl pointer-events-auto">

                            {/* Close */}
                            <button
                                onClick={closePaywall}
                                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-white/30 hover:text-white hover:bg-white/[0.06] transition-colors"
                            >
                                <RiCloseLine size={18} />
                            </button>

                            {/* Icon + headline */}
                            <div className="flex flex-col items-center text-center mb-6">
                                <div className="w-14 h-14 rounded-2xl bg-white/[0.05] border border-white/[0.1] flex items-center justify-center mb-4">
                                    <RiCoinLine className="text-white/60" size={26} />
                                </div>
                                <h2 className="text-xl font-bold text-white">You've run out of credits</h2>
                                <p className="text-white/40 text-sm mt-1 max-w-xs">
                                    Upgrade to keep using AI interviews, resume generation, and more.
                                </p>
                            </div>

                            {/* Plan cards */}
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                {PLANS.map(plan => (
                                    <div
                                        key={plan.key}
                                        className={`relative flex flex-col gap-3 p-4 rounded-2xl border transition-all ${
                                            plan.highlight
                                                ? 'bg-white/[0.07] border-white/[0.2]'
                                                : 'bg-white/[0.03] border-white/[0.07]'
                                        }`}
                                    >
                                        {plan.highlight && (
                                            <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-white text-black text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                                                <RiStarLine size={9} /> Best value
                                            </div>
                                        )}
                                        <div>
                                            <p className="text-white font-bold text-sm">{plan.label}</p>
                                            <p className="text-white/40 text-xs">{plan.credits}/month</p>
                                        </div>
                                        <p className="text-2xl font-black text-white">{plan.price}</p>
                                        <ul className="space-y-1.5 flex-1">
                                            {plan.features.map(f => (
                                                <li key={f} className="flex items-start gap-1.5 text-xs text-white/50">
                                                    <RiCheckLine className="text-emerald-400 flex-shrink-0 mt-0.5" size={12} />
                                                    {f}
                                                </li>
                                            ))}
                                        </ul>
                                        <button
                                            onClick={() => handleUpgrade(plan.key)}
                                            disabled={loading !== null}
                                            className={`w-full py-2 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-1.5
                                                ${plan.highlight
                                                    ? 'bg-white text-black hover:bg-white/90 disabled:opacity-50'
                                                    : 'bg-white/[0.07] text-white border border-white/[0.1] hover:bg-white/[0.12] disabled:opacity-50'
                                                }`}
                                        >
                                            {loading === plan.key ? (
                                                <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                                <>Upgrade <RiExternalLinkLine size={12} /></>
                                            )}
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {error && (
                                <p className="text-red-400 text-xs text-center mb-3">{error}</p>
                            )}

                            <p className="text-center text-white/20 text-xs">
                                Secure checkout via Stripe · Cancel anytime
                            </p>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
